/**
 * Thumbnail UI state — one store per open board view, owned by BoardScreen,
 * exactly like the design store (`features/design/store.ts`).
 *
 * LOCAL view state only: whether the panel is open, which requests are in
 * flight, the last errors, and which frame has its refine box open. The
 * words, the portraits, the shots and the run's progress all live in the Yjs
 * doc and are read reactively through the session's `thumbsVersion`.
 */
import { createStore, type StoreApi } from "zustand/vanilla";

export interface ThumbUiState {
  panelOpen: boolean;
  /** A run is being started (the 202 has not come back yet). */
  starting: boolean;
  /** Last error from starting or stopping a run. */
  error: string | null;
  /** The frame whose refine box is open, if any. */
  refineOpenFor: string | null;
  /** Frames with a refinement in flight. */
  refining: Record<string, true>;
  /** Last refinement error, per frame. */
  refineErrors: Record<string, string>;

  setPanelOpen(open: boolean): void;
  setStarting(starting: boolean): void;
  setError(error: string | null): void;
  setRefineOpen(shotId: string | null): void;
  setRefining(shotId: string, on: boolean): void;
  setRefineError(shotId: string, error: string | null): void;
}

export type ThumbUiStore = StoreApi<ThumbUiState>;

export function createThumbUiStore(): ThumbUiStore {
  return createStore<ThumbUiState>((set) => ({
    panelOpen: false,
    starting: false,
    error: null,
    refineOpenFor: null,
    refining: {},
    refineErrors: {},

    setPanelOpen: (panelOpen) => set({ panelOpen }),
    setStarting: (starting) => set({ starting }),
    setError: (error) => set({ error }),
    setRefineOpen: (refineOpenFor) => set({ refineOpenFor }),
    setRefining: (shotId, on) =>
      set((s) => {
        const refining = { ...s.refining };
        if (on) refining[shotId] = true;
        else delete refining[shotId];
        return { refining };
      }),
    setRefineError: (shotId, error) =>
      set((s) => {
        const refineErrors = { ...s.refineErrors };
        if (error === null) delete refineErrors[shotId];
        else refineErrors[shotId] = error;
        return { refineErrors };
      }),
  }));
}
